import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  InputSignal,
  linkedSignal,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  TemplateRef,
  WritableSignal
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoPipe } from '@jsverse/transloco';
import type { GroupingCapWarning } from '../contracts/grouping-cap-warning.interface';
import { GRID_FEATURES, type GridFeatures } from '../contracts/grid-features.interface';
import type { GridAction } from '../contracts/grid-action.interface';
import type {
  GridActionTriggeredEvent,
  GridRowKeyFn,
  GridRowSelectionMode,
  GridSelectionDescriptor
} from '../contracts/grid-row-selection.interface';
import type { GridStatePersistence } from '../contracts/grid-state-persistence.interface';
import { mergeGridLayoutState } from '../grid-layout-persistence';
import { GRID_STATE_PERSISTENCE } from '../in-memory-grid-state-persistence';
import type { ColumnFilter, ColumnFilterMap } from '../d-data-grid/column-filter.types';
import { DeightonDataGridComponent } from '../d-data-grid/d-data-grid.component';
import { DATA_GRID_DARK_THEME, DATA_GRID_LIGHT_THEME } from '../d-data-grid/data-grid-themes';
import {
  GridScopeFilterMode,
  DataGridPanelState,
  DataGridColumn,
  DataGridState,
  DataGridTheme,
  DataGridGroupBucketViewState,
  GroupedColumnOverride,
  SortDirection
} from '../d-data-grid/data-grid.types';

/** Bottom-docked panel that wraps the element grid with toolbar controls. */
// styles declaring the theme css tokens are set on the host element
@Component({
  selector: 'd-data-grid-panel',
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, DeightonDataGridComponent, TranslocoPipe],
  templateUrl: './d-data-grid-panel.component.html',
  styleUrl: './d-data-grid-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.is-maximized]': 'isMaximized()',
    '[style.--dg-surface]': 'effectiveTheme().surface ?? null',
    '[style.--dg-surface-container]': 'effectiveTheme().surfaceContainer ?? null',
    '[style.--dg-on-surface]': 'effectiveTheme().onSurface ?? null',
    '[style.--dg-on-surface-variant]': 'effectiveTheme().onSurfaceVariant ?? null',
    '[style.--dg-primary]': 'effectiveTheme().primary ?? null',
    '[style.--dg-border-color]': 'effectiveTheme().borderColor ?? null',
    '[style.--dg-row-stripe]': 'effectiveTheme().rowStripe ?? null',
    '[style.--dg-row-hover]': 'effectiveTheme().rowHover ?? null',
    '[style.--dg-header-type-specific-bg]': 'effectiveTheme().headerDiscriminatorBg ?? null',
    '[style.--dg-header-hover-bg]': 'effectiveTheme().headerHoverBg ?? null',
    '[style.--dg-chip-bg]': 'effectiveTheme().chipBg ?? null',
    '[style.--dg-chip-bg-active]': 'effectiveTheme().chipBgActive ?? null',
    '[style.--dg-chip-color]': 'effectiveTheme().chipColor ?? null',
    '[style.--dg-chip-border]': 'effectiveTheme().chipBorder ?? null',
    '[style.--dg-danger]': 'effectiveTheme().danger ?? null'
  }
})
export class DeightonDataGridPanelComponent {
  /** Injected persistence port for grid UI state. */
  private readonly persistence: GridStatePersistence = inject(GRID_STATE_PERSISTENCE);

  /** Injected feature preset merged with per-instance overrides. */
  private readonly injectedFeatures: GridFeatures = inject(GRID_FEATURES);

  /** Per-instance feature overrides merged over {@link GRID_FEATURES}. */
  public features: InputSignal<Partial<GridFeatures>> = input<Partial<GridFeatures>>({});

  /** Resolved feature flags for template gating. */
  protected resolvedFeatures: Signal<GridFeatures> = computed((): GridFeatures => ({
    ...this.injectedFeatures,
    ...this.features()
  }));

  /** Custom template for the leading row-action cell; forwarded to the grid. */
  public rowActionTemplate: InputSignal<TemplateRef<{ $implicit: Record<string, unknown> }> | null> =
    input<TemplateRef<{ $implicit: Record<string, unknown> }> | null>(null);

  /** Width of the row-action column in pixels; forwarded to the grid. */
  public rowActionWidth: InputSignal<number> = input<number>(36);

  /** Consumer-defined toolbar actions; forwarded to the grid. */
  public gridActions: InputSignal<ReadonlyArray<GridAction>> = input<ReadonlyArray<GridAction>>(
    new Array<GridAction>()
  );

  /** Row selection mode; forwarded to the grid. */
  public rowSelectionMode: InputSignal<GridRowSelectionMode> = input<GridRowSelectionMode>('multiple');

  /** Field used as the row identity; forwarded to the grid. */
  public rowKeyField: InputSignal<string> = input<string>('');

  /** Optional row-key function; forwarded to the grid. */
  public rowKeyFn: InputSignal<GridRowKeyFn | null> = input<GridRowKeyFn | null>(null);

  /**
   * Initial visibility of selection checkboxes and the selection bar.
   * The toolbar toggle can still hide or show the chrome afterwards.
   */
  public initialSelectionUiEnabled: InputSignal<boolean> = input<boolean>(false);

  /** Theme configuration forwarded to the grid when the theme toggle is disabled. */
  public theme: InputSignal<DataGridTheme> = input<DataGridTheme>({});

  /** Initial dark/light state for the theme toggle (default dark). */
  public initialDarkTheme: InputSignal<boolean> = input<boolean>(true);

  /** Whether dark theme is active (toggle state). */
  protected isDarkTheme: WritableSignal<boolean> = signal<boolean>(true);

  /** Resolved theme: toggle-driven when enabled, otherwise the theme input or initialDarkTheme. */
  protected effectiveTheme: Signal<DataGridTheme> = computed((): DataGridTheme => {
    if (this.resolvedFeatures().themeToggle) {
      return this.isDarkTheme() ? DATA_GRID_DARK_THEME : DATA_GRID_LIGHT_THEME;
    }
    const explicitTheme: DataGridTheme = this.theme();
    if (Object.keys(explicitTheme).length > 0) {
      return explicitTheme;
    }
    return this.initialDarkTheme() ? DATA_GRID_DARK_THEME : DATA_GRID_LIGHT_THEME;
  });

  /** Flat element data to pass to the grid. */
  public data: InputSignal<Array<Record<string, unknown>>> = input<Array<Record<string, unknown>>>(
    new Array<Record<string, unknown>>()
  );

  /** Shared column definitions. */
  public sharedColumns: InputSignal<Array<DataGridColumn>> = input<Array<DataGridColumn>>(
    new Array<DataGridColumn>()
  );

  /** Type-specific column definitions. */
  public discriminatorColumns: InputSignal<Record<string, Array<DataGridColumn>>> = input<
    Record<string, Array<DataGridColumn>>
  >({});

  /**
   * Differentiator field name passed through to the grid.
   * Empty when the host has no type discriminator; inventory binds `INVENTORY_DISCRIMINATOR_FIELD`.
   */
  public discriminatorField: InputSignal<string> = input<string>('');

  /** Color map for element types, keyed by type name. Passed through to the grid. */
  public discriminatorColors: InputSignal<Record<string, string>> = input<Record<string, string>>({});

  /** Maps element type slug to its human-friendly display name. Passed through to the grid. */
  public discriminatorLabels: InputSignal<Record<string, string>> = input<Record<string, string>>({});

  /** Grouped-leaf shared column presentation overrides, keyed by field then element type. */
  public groupedColumnOverrides: InputSignal<
    ReadonlyMap<string, ReadonlyMap<string, GroupedColumnOverride>>
  > = input<ReadonlyMap<string, ReadonlyMap<string, GroupedColumnOverride>>>(
    new Map<string, ReadonlyMap<string, GroupedColumnOverride>>()
  );

  /** Total count of elements matching the current filters, for virtual scroll sizing. */
  public totalCount: InputSignal<number> = input<number>(0);

  /** Whether a data fetch is in progress. Passed through to the grid for loading indicators. */
  public loading: InputSignal<boolean> = input<boolean>(false);

  /** Whether the group-summary phase is in progress (headers not yet available). */
  public summaryLoading: InputSignal<boolean> = input<boolean>(false);

  /** Warning when grouped loading hit orchestration caps. */
  public groupingCapWarnings: InputSignal<Array<GroupingCapWarning>> = input<Array<GroupingCapWarning>>(
    new Array<GroupingCapWarning>()
  );

  /** Whether more primary group header pages can be loaded. */
  public hasMoreGroupHeaders: InputSignal<boolean> = input<boolean>(false);

  /** Key of the element currently hovered on the map, passed through to the grid. */
  public hoveredRowKey: InputSignal<number | null> = input<number | null>(null);

  /** Whether more pages of grid data are available from the server. */
  public hasMore: InputSignal<boolean> = input<boolean>(false);

  /** Whether a pagination request is currently in flight. Prevents duplicate loadMore events. */
  public loadingMore: InputSignal<boolean> = input<boolean>(false);

  /** Per-bucket loading / hasNextPage flags for grouped leaf rendering. */
  public groupBucketStates: InputSignal<Record<string, DataGridGroupBucketViewState>> = input<
    Record<string, DataGridGroupBucketViewState>
  >({});

  /** Server-provided totalCount per element type, for accurate group header counts. */
  public typeTotalCounts: InputSignal<Record<string, number>> = input<Record<string, number>>({});

  /** Server-provided totalCount per sub-group, for accurate nested group header counts. */
  public subGroupCounts: InputSignal<Record<string, number>> = input<Record<string, number>>({});

  /** The outermost group field used for per-group parallel pagination. Null when ungrouped. */
  public primaryGroupField: InputSignal<string | null> = input<string | null>(null);

  /** Group fields detected as high-cardinality — collapsed and lazily loaded by default. */
  public highCardinalityFields: InputSignal<Set<string>> = input<Set<string>>(new Set<string>());

  /**
   * camelCase field names the UI allows grouping by.
   * Passed through to the grid component to restrict the group-by affordance.
   */
  public groupableFields: InputSignal<Array<string>> = input<Array<string>>(new Array<string>());

  /** Whether the dark/light theme toggle button is visible in the toolbar. */
  public showThemeToggle: InputSignal<boolean> = input<boolean>(false);

  /** Whether the theme toggle is shown — legacy input merged into {@link features}. */
  protected showThemeToggleButton: Signal<boolean> = computed(
    (): boolean => this.resolvedFeatures().themeToggle || this.showThemeToggle()
  );

  /** Emits when the grid needs the next page of data. Carries the element type for per-type mode, or null for flat mode. */
  public loadMore: OutputEmitterRef<string | null> = output<string | null>();

  /** Emits when the user requests the next page of group headers. */
  public loadMoreGroupHeaders: OutputEmitterRef<void> = output<void>();

  /** Emits when the grid grouping state changes (for per-type query switching). */
  public gridGroupingChange: OutputEmitterRef<DataGridState> = output<DataGridState>();

  /** Emits when the user changes the data filter mode. */
  public filterModeChange: OutputEmitterRef<GridScopeFilterMode> = output<GridScopeFilterMode>();

  /** Emits when the user changes the sort column/direction, or null to clear the sort. */
  public sortChange: OutputEmitterRef<{ field: string; direction: SortDirection } | null> = output<{
    field: string;
    direction: SortDirection;
  } | null>();

  /** Emits when the user clicks the fly-to button on a grid row, carrying the row data for map navigation. */
  public rowAction: OutputEmitterRef<Record<string, unknown>> = output<Record<string, unknown>>();

  /** Emits the element key when the user hovers a grid row, or null on mouseleave. */
  public rowHover: OutputEmitterRef<number | null> = output<number | null>();

  /** Emits when the user closes the panel. */
  public panelClose: OutputEmitterRef<void> = output<void>();

  /** Emits when column filters change (for orchestration service integration). */
  public columnFiltersChange: OutputEmitterRef<ColumnFilterMap> = output<ColumnFilterMap>();

  /** Emits whenever the row selection changes. */
  public selectionChange: OutputEmitterRef<GridSelectionDescriptor> = output<GridSelectionDescriptor>();

  /** Emits when the user triggers a declarative grid action. */
  public gridActionTriggered: OutputEmitterRef<GridActionTriggeredEvent> = output<GridActionTriggeredEvent>();

  /** Number of active column filters (from orchestration service). */
  public activeFilterCount: InputSignal<number> = input<number>(0);

  /** Initial panel display state (half, maximized, or collapsed). */
  public initialPanelState: InputSignal<DataGridPanelState> = input<DataGridPanelState>('half');

  /** Current panel display state. */
  protected panelState: WritableSignal<DataGridPanelState> = linkedSignal((): DataGridPanelState =>
    this.initialPanelState()
  );

  /** Current filter mode: global shows all data, boundingBox filters to viewport. */
  protected filterMode: WritableSignal<GridScopeFilterMode> = signal<GridScopeFilterMode>(
    this.injectedFeatures.scopeToggle ? 'boundingBox' : 'global'
  );

  /** Whether selection checkboxes and the selection bar are visible. */
  protected selectionUiVisible: WritableSignal<boolean> = linkedSignal((): boolean =>
    this.initialSelectionUiEnabled()
  );

  /** Active column filters received from the orchestration service (read-only). */
  public columnFilters: InputSignal<ColumnFilterMap> = input<ColumnFilterMap>(
    new Map<string, ColumnFilter>()
  );

  /** Persisted hidden columns passed to the grid. */
  protected persistedHiddenColumns: Signal<Set<string>> = computed(
    (): Set<string> => new Set<string>(this.persistence.state().hiddenColumns)
  );

  /** Persisted group-by fields passed to the grid as initial state. */
  protected initialGroupByFields: Signal<Array<string>> = computed(
    (): Array<string> => this.persistence.state().groupByFields
  );

  /** Persisted shared column order passed to the grid. */
  protected initialColumnOrder: Signal<Array<string>> = computed(
    (): Array<string> => this.persistence.state().columnOrder ?? new Array<string>()
  );

  /** Persisted type-specific column order passed to the grid. */
  protected initialTypeSpecificColumnOrder: Signal<Record<string, Array<string>>> = computed(
    (): Record<string, Array<string>> => this.persistence.state().discriminatorColumnOrder ?? {}
  );

  /** Persisted user-defined column widths passed to the grid. */
  protected initialColumnWidths: Signal<Record<string, number>> = computed(
    (): Record<string, number> => this.persistence.state().columnWidths ?? {}
  );

  /** Whether the info banner has been dismissed by the user (persisted). */
  protected infoBannerDismissed: Signal<boolean> = computed(
    (): boolean => this.persistence.state().infoBannerDismissed === true
  );

  /** Whether any icon-only toolbar controls should render after the Columns button. */
  protected showToolbarIconCluster: Signal<boolean> = computed((): boolean => {
    const features: GridFeatures = this.resolvedFeatures();
    return (
      features.rowSelection ||
      this.showThemeToggleButton() ||
      features.scopeToggle ||
      features.maximizeButton ||
      features.closeButton
    );
  });

  /** Initializes DeightonDataGridPanelComponent and syncs the theme toggle. */
  constructor() {
    effect((): void => {
      this.isDarkTheme.set(this.initialDarkTheme());
    });
  }

  /** Toggles between global and bounding-box data filtering. */
  protected toggleFilterMode(): void {
    const next: GridScopeFilterMode = this.filterMode() === 'global' ? 'boundingBox' : 'global';
    this.filterMode.set(next);
    this.filterModeChange.emit(next);
  }

  /** Shows or hides selection checkboxes, group checkboxes, and the selection action bar. */
  protected toggleSelectionUi(): void {
    this.selectionUiVisible.update((visible: boolean): boolean => !visible);
  }

  /** Handles a column filter change event from the grid. */
  protected onFilterChange(event: { field: string; filter: ColumnFilter | null }): void {
    const next: ColumnFilterMap = new Map<string, ColumnFilter>(this.columnFilters());
    if (event.filter === null) {
      next.delete(event.field);
    } else {
      next.set(event.field, event.filter);
    }
    this.columnFiltersChange.emit(next);
  }

  /** Handles dismiss state changes from the grid's info banner. */
  protected onInfoBannerDismissedChange(dismissed: boolean): void {
    this.persistGridState({ infoBannerDismissed: dismissed });
  }

  /** Restores the info banner after the user dismissed it. */
  protected restoreInfoBanner(): void {
    this.persistGridState({ infoBannerDismissed: false });
  }

  /** Toggles the panel between half and maximized states. */
  protected toggleMaximize(): void {
    const next: DataGridPanelState = this.panelState() === 'maximized' ? 'half' : 'maximized';
    this.panelState.set(next);
  }

  /** Returns true if the panel is maximized. */
  protected readonly isMaximized: Signal<boolean> = computed(
    (): boolean => this.panelState() === 'maximized'
  );

  /**
   * Merges overrides onto the current persisted state so layout and filter fields are not dropped.
   *
   * @param overrides - Properties to override on the current grid state.
   */
  private persistGridState(overrides: Partial<DataGridState>): void {
    this.persistence.save(mergeGridLayoutState(this.persistence.state(), overrides));
  }
}
